/**
 * The project list, read off the document.
 *
 * ADR 0012's read shape: a pure `select…(view: DocumentView, …)` with a one-line hook over it.
 *
 * **`projectsQueryKey` is not here.** It was declared beside the old hook and registered against no
 * query — the tree holds exactly one `useQuery`, in `legacy/supabase-store/use-people.ts` — so its
 * invalidations marked nothing stale and it is deleted rather than moved (#315, ADR 0012 rule 6).
 */

import { useMemo } from 'react'
import { useDocumentStatus, useDocumentView } from '@/document'
import type { DocumentView, Project } from '@/document'

/** Every live project, by name. */
export function selectProjects(view: DocumentView | null): Project[] {
  if (!view) return []

  return Object.values(view.projects)
    .filter((project) => !project.archivedAt)
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * `selectProjects` bound to this device's document.
 *
 * The four members beside `data` are what its callers destructure; they predate the Automerge move
 * and are kept so this stays a move rather than a signature change.
 */
export function useProjects() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(() => selectProjects(doc), [doc])

  return {
    data,
    isLoading: !ready,
    error: null,
    isSuccess: ready,
    isError: false,
  }
}
